import React, { Component, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { formatNum } from 'utils/helper';
import style from './style.scss';
import { ze } from "utils/zhEn";
import { Table } from 'antd';
import RestHelper from 'utils/RestHelper';
import { ServerAppSettings } from 'utils/server-app-settings';
import moment from 'moment';
import { TimeUtil } from 'utils/time-util';
import {
  selectNullableServerAppSettings,
} from 'utils/selectors/selectors';
import { connect, useSelector } from 'react-redux';
import { isNotNullish } from 'utils/nullish';
import { useTranslation } from 'react-i18next';
import { transformLocaleStrngToNumber } from 'utils/format/format-number'
import { VersionUtil } from 'utils/version-util';
import PercentageBar from 'components/commons/PercentageBar/PercentageBar';
import VersionHybridSelect from 'components/commons/VersionHybridSelect';
import { getTreeSelectVersionOptions } from 'utils/getTreeSelectVersionOptions';
import groupBy from 'lodash/collection/groupBy';
import IssueTrendBarChart from 'components/exception/BaseProblemPage/IssueItem/IssueTrendBarChart';
import { IssueListTrendTypeEnum } from 'utils/constants';
import { ix } from 'utils/i18n-with-context';
import { BillingTeamType } from 'utils/constants/billing';

const MAX_VERSIONS_FOR_QUERY_ACCESS_TREND = 100;

const SystemInfoTable = (props) => {
  const { t } = useTranslation();
  const {
    reduxState,
    appId,
    platformId,
    versions: versionsImmutableJs,
    issueId,
  } = props;

  const issueVersionList = useMemo(() => {
    if (versionsImmutableJs?.size > 0) {
      return versionsImmutableJs.toJS();
    } else {
      return [];
    }
  }, [versionsImmutableJs]);

  const currentApp = useMemo(() => reduxState.app.get('current').toJS(), [reduxState.app.get('current')]);
  const { teamType } = currentApp;
  const enableQueryProjectVersionBuildTime = teamType === BillingTeamType.TENCENT || teamType === BillingTeamType.TEST;

  const isTooManyVersionsForQueryAccessTrend = issueVersionList.length > MAX_VERSIONS_FOR_QUERY_ACCESS_TREND;

  const nullableServerAppSettings = useSelector(reduxState => selectNullableServerAppSettings(reduxState, appId));
  const hasServerAppSettingsFetched = isNotNullish(nullableServerAppSettings);
  const enableIssueLifetimeAllDataStats = (nullableServerAppSettings || {})[ServerAppSettings.keys.enableIssueLifetimeAllDataStats];

  const [issueLifetimeAllDataStatList, setIssueLifetimeAllDataStatList] = useState([]);
  const [innerValue, setInnerValue] = useState([]);

  const [issueVersionToBuildTimeInfo, setIssueVersionToBuildTimeInfo] = useState({});

  const [versionToDailyTrend, setVersionToDailyTrend] = useState({});
  const [maxAccessUser, setMaxAccessUser] = useState(0);

  useEffect(() => {
    setIssueLifetimeAllDataStatList([]);
  }, [appId]);

  useEffect(() => {
    if (!appId || !hasServerAppSettingsFetched) {
      return;
    }
    if (enableIssueLifetimeAllDataStats) {
      queryIssueLifetimeAllDataStat();
    }
  }, [appId, hasServerAppSettingsFetched]);

  useEffect(() => {
    const versionList = issueVersionList.map(x => x.version);
    if (versionList.length > 0 && !isTooManyVersionsForQueryAccessTrend) {
      queryAccessTrendByVersion(versionList);
    }
    if (versionList.length > 0 && enableQueryProjectVersionBuildTime) {
      queryProjectVersionBuildTime(versionList);
    }
  }, [issueVersionList]);

  function getDataSource(issueVersionList) {
    let countSum = 0;
    let arrList = [];
    const len = issueVersionList.length;
    if (len) {
      issueVersionList.forEach((arr) => {
        countSum += arr.count;
      });
    }// 没有数据的时候怎么显示，还没确定，还有动画

    arrList = issueVersionList;
    const mergedArr = [...arrList, ...issueLifetimeAllDataStatList].reduce((map, item) => {
      map.set(item.version, { ...(map.get(item.version) || {}), ...item });
      return map;
    }, new Map());
    const result = Array.from(mergedArr.values());
    return result.map((issue) => {
      const proportionPercentage = issue.count && countSum
        ? issue.count / countSum * 100
        : 0;
      const version = issue.version;

      return {
        key: issue.version,
      version: version || '-',
        buildDateText: issueVersionToBuildTimeInfo[version] ? issueVersionToBuildTimeInfo[version].buildDateText : '-',
      postFirstTime: issue.firstUploadTimestamp ? moment(TimeUtil.fixChaosMillis(issue.firstUploadTimestamp)).format('YYYY-MM-DD HH:mm:ss') : '-',
      postLastTime: issue.lastUploadTimestamp ? moment(TimeUtil.fixChaosMillis(issue.lastUploadTimestamp)).format('YYYY-MM-DD HH:mm:ss') : '-',
      occurNum:issue.count ? formatNum(issue.count) : '-',
      proportionPercentage,
      userNumAffect:issue.deviceCount ? formatNum(issue.deviceCount) : '-',
      numAllData:issue.numAllData ? formatNum(issue.numAllData) : '-',
      devicesAllData:issue.devicesAllData ? formatNum(issue.devicesAllData) : '-',
        versionDailyTrend: versionToDailyTrend[issue.version],
    }});
  }

  async function queryIssueLifetimeAllDataStat() {
    const rsp = await RestHelper.post('/redir/api/statResource/queryIssueLifetimeAllDataStats',{ appId, issueId, platformId });
    setIssueLifetimeAllDataStatList(rsp.json.data);
  }

  // 查询版本的符号表构建时间
  async function queryProjectVersionBuildTime(versionList) {
    const rsp = await RestHelper.post('/redir/api/symbol/queryProjectVersionBuildTime',{ appId, versionList });
    const entries = rsp.json.data.map((x) => {
      const v = {
        ...x,
        buildDateText: x.firstBuildTime ? moment(x.firstBuildTime).format('YYYY-MM-DD HH:mm:ss') : '-',
      }
      return [x.productVersion, v];
    });
    setIssueVersionToBuildTimeInfo(Object.fromEntries(entries));
  }

  async function queryAccessTrendByVersion(versionList) {
    setMaxAccessUser(0);
    if ((versionList || []).length === 0) {
      return;
    }
    const rsp = await RestHelper.post('/api/app/getTrend', {
      appId,
      platformId,
      startDate: moment().subtract(14, 'days').format('YYYYMMDD'),
      endDate: moment().format('YYYYMMDD'),
      type: 'crash', // 因为只看联网，这个type可以随便
      versionList,
      __queryAccessTrendByVersion__: true, // 没用的参数，方便debug从哪里调用的
    });
    const data = rsp.json.ret.data;
    const maxAccessUser = Math.max(...data.map(x => x.accessUser));
    const versionToDailyTrend = groupBy(data, x => x.version);
    setVersionToDailyTrend(versionToDailyTrend);
    setMaxAccessUser(maxAccessUser || 0);
  }

  // 数值对比方法
  function getCountCompareFn(key) {
    return (a, b) => {
      const aValue = transformLocaleStrngToNumber(a[key]);
      const bValue = transformLocaleStrngToNumber(b[key]);

      if (Number.isNaN(aValue)) return -1;
      if (Number.isNaN(bValue)) return 1;

      return aValue - bValue;
    };
  }

  function getColumns() {
    return [{
      title: t("SYMBOLINFOTABLE.version"),
      dataIndex: 'version',
      showcase:true,
      sorter: {
        compare: (a, b) => VersionUtil.compareVersion(a.version, b.version),
      }
    },{
      title: t("SYMBOLINFOTABLE.postFirstTime") ,
      dataIndex: 'postFirstTime',
      showcase:true,
      sorter: {
        compare: (a, b) => new Date(a.postFirstTime) - new Date(b.postFirstTime),
      },
    },{
      title: t("SYMBOLINFOTABLE.postLastTime") ,
      dataIndex: 'postLastTime',
      showcase:true,
      sorter: {
        compare: (a, b) => new Date(a.postLastTime) - new Date(b.postLastTime),
      },
    }, enableQueryProjectVersionBuildTime && {
      title: ze('构建时间', 'Build Time'),
      dataIndex: 'buildDateText',
      showcase: enableQueryProjectVersionBuildTime,
      sorter: {
        compare: (a, b) => a?.buildDateText?.localeCompare(b?.buildDateText),
      },
    }, {
      title: <div>
        {t("SYMBOLINFOTABLE.occurNum")}
        {enableIssueLifetimeAllDataStats && `(${ze('丢弃后','post-disposable')})`}
      </div>,
      dataIndex: 'occurNum',
      align: 'right',
      showcase:true,
      sorter: {
        compare: getCountCompareFn('occurNum'),
      },
    },{
      title: `${t("SYMBOLINFOTABLE.occurNum")}(${ze('丢弃前','pre-rejection')})`,
      dataIndex: 'numAllData',
      align: 'right',
      showcase:enableIssueLifetimeAllDataStats,
      sorter: {
        compare: getCountCompareFn('numAllData'),
      },
    },{
      title: t("SYMBOLINFOTABLE.numRate") ,
      dataIndex: 'proportionPercentage',
      align: 'right',
      showcase:true,
      render: (text, record) => {
        return <PercentageBar
          style={{ width: '100%' }}
          percentage={record.proportionPercentage}
        />;
      },
      sorter: {
        compare: (a, b) => a.proportionPercentage - b.proportionPercentage,
      },
    },{
      title: <div>
        {ix(t("SYMBOLINFOTABLE.userNumAffect"))}
        {enableIssueLifetimeAllDataStats && `(${ze('丢弃后','post-disposable')})`}
      </div>,
      dataIndex: 'userNumAffect',
      align: 'right',
      showcase:true,
      sorter: {
        compare: getCountCompareFn('userNumAffect'),
      },
    },{
      title: `${t("SYMBOLINFOTABLE.userNumAffect")}(${ze('丢弃前','pre-rejection')})` ,
      dataIndex: 'devicesAllData',
      showcase:enableIssueLifetimeAllDataStats,
      align: 'right',
      sorter: {
        compare: getCountCompareFn('devicesAllData'),
      },
    },{
      title: ze('最近15天联网', 'Active Devices in Last 15d') ,
      dataIndex: 'versionDailyTrend',
      showcase: !isTooManyVersionsForQueryAccessTrend,
      align: 'right',
      width: 220,
      render: (text, record) => {
        const { versionDailyTrend } = record;
        return versionDailyTrend && <div
          style={{ marginBottom: '-12px', marginTop: '-8px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
        ><IssueTrendBarChart
          style={{ height: '48px' }}
          trendType={IssueListTrendTypeEnum.DAILY}
          dailyTrendList={versionDailyTrend}
          field='accessUser'
          fieldLabel={t('EXCP_OVERVIEW.userNumConnect')}
        /></div>;
      }
    },].filter(item => item.showcase)
  }

  function render() {
    const pageSize = 5;
    const data = getDataSource(issueVersionList);
    const isMultiplePages = (data || []).length > pageSize;
    const versionTreeData = data.map(item => {
      return {
        value:item.version,
        label:item.version
      }
    });
    const versionTree = getTreeSelectVersionOptions(versionTreeData || []);
    const tableData = innerValue.length > 0
      ? data.filter(item => {
          const itemVersion = item.version;
          return innerValue.some(val => {
            if (val.endsWith('*')) {
              const baseVal = val.slice(0, -1);
              return itemVersion.startsWith(baseVal);
            } else {
              return val === itemVersion;
            }
          });
        })
      : data;
    return (
      <div>
        <div style={{marginBottom:'10px'}}>
          <VersionHybridSelect
            style={{ minWidth:'320px', zIndex:'0' }}
            treeData={versionTree}
            value={ innerValue }
            onChange={(v) => { setInnerValue(v) }}
          />
        </div>
        <Table
          className={style.issue_table}
          size="small"
          columns={getColumns()}
          dataSource={tableData}
          pagination={isMultiplePages ? { pageSize } : false}
        />
      </div>
    );
  }

  return render();
}

SystemInfoTable.propTypes = {
  versions: PropTypes.object,
  issueId: PropTypes.object,
  platformId: PropTypes.number,
  appId: PropTypes.any,
};

const mapStateToProps = state => ({
  reduxState: state,
});

export default connect(mapStateToProps)(SystemInfoTable);
